import React, { useId } from 'react';
import { useMotorSound } from './useMotorSound';

export interface ConveyorSvgProps {
  isRunning?: boolean;
  isJammed?: boolean;
  length?: number; // Conveyor bed length in px (default: 400)
  width?: number; // Conveyor bed depth in px (default: 60)
  speed?: number; // Conveyor speed in px/s (default: 100)
  direction?: 'RIGHT' | 'LEFT' | string; // Running direction
  color?: string; // Main structural frame color (default: '#334155' industrial slate)
  motorColor?: string; // Motor stator body color (default: '#146E46' industrial green)
  className?: string;
  style?: React.CSSProperties;
}

export const ConveyorSvg: React.FC<ConveyorSvgProps> = ({
  isRunning = false,
  isJammed = false,
  length = 400,
  width: _width = 60,
  speed = 100,
  direction = 'RIGHT',
  color = '#334155',
  motorColor = '#146E46',
  className = '',
  style,
}) => {
  const uid = useId().replace(/:/g, '_');
  const { isSpinning } = useMotorSound(isRunning);
  const isMoving = isRunning || isSpinning;

  const isReverse = direction.toUpperCase() === 'LEFT';
  const effectiveLength = Math.max(160, Number(length) || 400);
  const totalHeight = 85;

  // Animation durations based on speed
  const normalizedSpeed = Math.max(10, Math.min(300, Number(speed) || 100));
  const beltDuration = (20 / normalizedSpeed).toFixed(3);
  const drumDuration = (35 / normalizedSpeed).toFixed(3);

  // Status colors
  const statusColor = isJammed ? '#ef4444' : isRunning ? '#22c55e' : '#64748b';
  const frameColor = color || '#334155';
  const effectiveMotorColor = isJammed ? '#b91c1c' : motorColor;

  // Intermediate rollers calculation
  const rollerSpacing = 70;
  const usableStart = 60;
  const usableEnd = effectiveLength - 60;
  const intermediateRollers: number[] = [];
  for (let rx = usableStart + rollerSpacing / 2; rx < usableEnd; rx += rollerSpacing) {
    intermediateRollers.push(Math.round(rx));
  }

  // Bracket uprights for side guide rails
  const bracketPositions: number[] = [35];
  for (let bx = 110; bx < effectiveLength - 60; bx += 90) {
    bracketPositions.push(Math.round(bx));
  }
  bracketPositions.push(effectiveLength - 35);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${effectiveLength} ${totalHeight}`}
      width={effectiveLength}
      height={totalHeight}
      className={`industrial-conveyor ${className}`}
      style={{ overflow: 'visible', ...style }}
    >
      <defs>
        {/* CSS Keyframe animations */}
        <style>
          {`
            @keyframes beltTreadFwd_${uid} {
              0% { stroke-dashoffset: 0; }
              100% { stroke-dashoffset: -28; }
            }
            @keyframes beltTreadRev_${uid} {
              0% { stroke-dashoffset: 0; }
              100% { stroke-dashoffset: 28; }
            }
            @keyframes drumSpinCW_${uid} {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            @keyframes drumSpinCCW_${uid} {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(-360deg); }
            }
            @keyframes ledPulse_${uid} {
              0%, 100% { opacity: 0.85; filter: drop-shadow(0 0 2px ${statusColor}); }
              50% { opacity: 1; filter: drop-shadow(0 0 6px ${statusColor}); }
            }
          `}
        </style>

        {/* Frame metallic gradient */}
        <linearGradient id={`frameGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#94a3b8" />
          <stop offset="15%" stopColor={frameColor} />
          <stop offset="70%" stopColor={frameColor} />
          <stop offset="100%" stopColor="#0f172a" />
        </linearGradient>

        {/* Heavy Belt Rubber 3D Gradient */}
        <linearGradient id={`beltRubberGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#334155" />
          <stop offset="30%" stopColor="#1e293b" />
          <stop offset="85%" stopColor="#0f172a" />
          <stop offset="100%" stopColor="#020617" />
        </linearGradient>

        {/* Steel Pulley Drum Cylindrical Gradient */}
        <linearGradient id={`pulleyDrumGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#f1f5f9" />
          <stop offset="25%" stopColor="#cbd5e1" />
          <stop offset="65%" stopColor="#64748b" />
          <stop offset="100%" stopColor="#334155" />
        </linearGradient>

        {/* Motor Stator 3D Cylindrical Gradient */}
        <linearGradient id={`motorStatorGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#86efac" stopOpacity="0.4" />
          <stop offset="25%" stopColor={effectiveMotorColor} />
          <stop offset="70%" stopColor={effectiveMotorColor} />
          <stop offset="100%" stopColor="#052e16" />
        </linearGradient>

        {/* Gearbox Cast Iron Gradient */}
        <linearGradient id={`gearboxGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#64748b" />
          <stop offset="40%" stopColor="#334155" />
          <stop offset="90%" stopColor="#1e293b" />
          <stop offset="100%" stopColor="#0f172a" />
        </linearGradient>

        {/* Leg / Stand Metallic Gradient */}
        <linearGradient id={`legGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#1e293b" />
          <stop offset="35%" stopColor="#64748b" />
          <stop offset="70%" stopColor="#475569" />
          <stop offset="100%" stopColor="#0f172a" />
        </linearGradient>

        {/* Glow Filter for Active LEDs */}
        <filter id={`statusGlow-${uid}`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* ================= 1. SUPPORT LEGS & FEET ================= */}
      <g id="conveyor-stands">
        {/* Left Stand Assembly */}
        <g transform="translate(50, 0)">
          {/* Vertical Leg Extrusion */}
          <rect x="-7" y="60" width="14" height="21" rx="1.5" fill={`url(#legGrad-${uid})`} stroke="#0f172a" strokeWidth="0.8" />
          {/* Height Adjustment Holes */}
          <circle cx="0" cy="67" r="1.5" fill="#0f172a" />
          <circle cx="0" cy="74" r="1.5" fill="#0f172a" />
          {/* Leveling Foot Plate */}
          <rect x="-14" y="80" width="28" height="4" rx="1.5" fill="#1e293b" stroke="#475569" strokeWidth="0.8" />
          {/* Floor Anchor Bolts */}
          <circle cx="-9" cy="82" r="1.2" fill="#94a3b8" />
          <circle cx="9" cy="82" r="1.2" fill="#94a3b8" />
        </g>

        {/* Right Stand Assembly */}
        <g transform={`translate(${effectiveLength - 50}, 0)`}>
          <rect x="-7" y="60" width="14" height="21" rx="1.5" fill={`url(#legGrad-${uid})`} stroke="#0f172a" strokeWidth="0.8" />
          <circle cx="0" cy="67" r="1.5" fill="#0f172a" />
          <circle cx="0" cy="74" r="1.5" fill="#0f172a" />
          <rect x="-14" y="80" width="28" height="4" rx="1.5" fill="#1e293b" stroke="#475569" strokeWidth="0.8" />
          <circle cx="-9" cy="82" r="1.2" fill="#94a3b8" />
          <circle cx="9" cy="82" r="1.2" fill="#94a3b8" />
        </g>

        {/* Center Stand for long spans (>= 350px) */}
        {effectiveLength >= 350 && (
          <g transform={`translate(${Math.round(effectiveLength / 2)}, 0)`}>
            <rect x="-6" y="60" width="12" height="21" rx="1.5" fill={`url(#legGrad-${uid})`} stroke="#0f172a" strokeWidth="0.8" />
            <circle cx="0" cy="67" r="1.2" fill="#0f172a" />
            <circle cx="0" cy="74" r="1.2" fill="#0f172a" />
            <rect x="-12" y="80" width="24" height="4" rx="1.5" fill="#1e293b" stroke="#475569" strokeWidth="0.8" />
          </g>
        )}

        {/* Bottom Horizontal Tie Bar */}
        <line
          x1="50"
          y1="75"
          x2={effectiveLength - 50}
          y2="75"
          stroke="#334155"
          strokeWidth="3.5"
          strokeLinecap="round"
        />
        <line
          x1="50"
          y1="74"
          x2={effectiveLength - 50}
          y2="74"
          stroke="#64748b"
          strokeWidth="1"
          strokeLinecap="round"
        />
      </g>

      {/* ================= 2. RETURN BELT (BOTTOM LOOP) ================= */}
      <g id="conveyor-return-belt">
        <path
          d={`M 22 65.5 L ${effectiveLength - 22} 65.5`}
          stroke={`url(#beltRubberGrad-${uid})`}
          strokeWidth="3.5"
          strokeLinecap="round"
        />
        {/* Subtle sag highlight */}
        <path
          d={`M 24 64.5 L ${effectiveLength - 24} 64.5`}
          stroke="#475569"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.6"
        />
      </g>

      {/* ================= 3. INTERMEDIATE CARRY ROLLERS ================= */}
      <g id="intermediate-rollers">
        {intermediateRollers.map((rx) => (
          <g key={rx} transform={`translate(${rx}, 56.5)`}>
            {/* Roller Drum */}
            <circle r="7.5" fill={`url(#pulleyDrumGrad-${uid})`} stroke="#1e293b" strokeWidth="0.8" />
            {/* Spinning Spoke indicator */}
            <g
              style={{
                transformOrigin: '0 0',
                animation: isMoving
                  ? `${isReverse ? `drumSpinCCW_${uid}` : `drumSpinCW_${uid}`} ${drumDuration}s linear infinite`
                  : 'none',
              }}
            >
              <line x1="-5" y1="0" x2="5" y2="0" stroke="#475569" strokeWidth="1.2" strokeLinecap="round" />
              <line x1="0" y1="-5" x2="0" y2="5" stroke="#475569" strokeWidth="1.2" strokeLinecap="round" />
            </g>
            {/* Central Axle Pin */}
            <circle r="2.2" fill="#0f172a" />
            <circle r="1" fill="#cbd5e1" />
          </g>
        ))}
      </g>

      {/* ================= 4. PULLEYS (TAIL & DRIVE DRUMS) ================= */}
      <g id="conveyor-pulleys">
        {/* Left Pulley (Idler / Tail Drum) */}
        <g transform="translate(22, 56.5)">
          <circle r="9" fill={`url(#pulleyDrumGrad-${uid})`} stroke="#0f172a" strokeWidth="1" />
          <circle r="7.5" fill="none" stroke="#64748b" strokeWidth="0.8" opacity="0.6" />
          {/* Animated Rotating Spokes */}
          <g
            style={{
              transformOrigin: '0 0',
              animation: isMoving
                ? `${isReverse ? `drumSpinCCW_${uid}` : `drumSpinCW_${uid}`} ${drumDuration}s linear infinite`
                : 'none',
            }}
          >
            <line x1="-6.5" y1="0" x2="6.5" y2="0" stroke="#334155" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="0" y1="-6.5" x2="0" y2="6.5" stroke="#334155" strokeWidth="1.5" strokeLinecap="round" />
          </g>
          {/* Shaft Center */}
          <circle r="3.5" fill="#1e293b" stroke="#0f172a" strokeWidth="0.8" />
          <circle r="1.5" fill="#e2e8f0" />
        </g>

        {/* Right Pulley (Drive / Head Drum) */}
        <g transform={`translate(${effectiveLength - 22}, 56.5)`}>
          <circle r="9" fill={`url(#pulleyDrumGrad-${uid})`} stroke="#0f172a" strokeWidth="1" />
          <circle r="7.5" fill="none" stroke="#64748b" strokeWidth="0.8" opacity="0.6" />
          {/* Animated Rotating Spokes */}
          <g
            style={{
              transformOrigin: '0 0',
              animation: isMoving
                ? `${isReverse ? `drumSpinCCW_${uid}` : `drumSpinCW_${uid}`} ${drumDuration}s linear infinite`
                : 'none',
            }}
          >
            <line x1="-6.5" y1="0" x2="6.5" y2="0" stroke="#334155" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="0" y1="-6.5" x2="0" y2="6.5" stroke="#334155" strokeWidth="1.5" strokeLinecap="round" />
          </g>
          {/* Drive Shaft Coupling */}
          <circle r="3.8" fill="#0f172a" stroke="#0f172a" strokeWidth="0.8" />
          <circle r="1.8" fill={isRunning ? '#22c55e' : '#cbd5e1'} />
        </g>
      </g>

      {/* ================= 5. STRUCTURAL SIDE CHANNELS (FRAME) ================= */}
      <g id="conveyor-frame">
        {/* Main Extruded Aluminum / Steel Rail Profile */}
        <rect
          x="12"
          y="52"
          width={effectiveLength - 24}
          height="12"
          rx="2"
          fill={`url(#frameGrad-${uid})`}
          stroke="#0f172a"
          strokeWidth="1"
        />

        {/* T-Slot Center Groove */}
        <rect
          x="16"
          y="56.5"
          width={effectiveLength - 32}
          height="3"
          rx="1"
          fill="#090d16"
          stroke="#475569"
          strokeWidth="0.5"
        />

        {/* Tail End Bearing Block (Pillow Block Housing) */}
        <rect x="14" y="52.5" width="16" height="11" rx="1.5" fill="#1e293b" stroke="#64748b" strokeWidth="0.8" />
        <circle cx="17" cy="58" r="1.2" fill="#94a3b8" />
        <circle cx="27" cy="58" r="1.2" fill="#94a3b8" />

        {/* Head End Bearing / Drive Flange Block */}
        <rect x={effectiveLength - 30} y="52.5" width="16" height="11" rx="1.5" fill="#1e293b" stroke="#64748b" strokeWidth="0.8" />
        <circle cx={effectiveLength - 27} cy="58" r="1.2" fill="#94a3b8" />
        <circle cx={effectiveLength - 17} cy="58" r="1.2" fill="#94a3b8" />

        {/* Industrial Frame Markings & Animated Direction Indicator */}
        <g transform={`translate(${Math.round(effectiveLength / 2)}, 58)`}>
          <text
            x="0"
            y="0"
            fill="#94a3b8"
            fontSize="7"
            fontFamily="monospace"
            fontWeight="bold"
            letterSpacing="1"
            textAnchor="middle"
            dominantBaseline="central"
            opacity="0.9"
          >
            {isReverse ? '◄ ◄ CONVEYOR ◄ ◄' : '► ► CONVEYOR ► ►'}
          </text>
        </g>
      </g>

      {/* ================= 6. CARRYING BELT (TOP LAYER & MOVING TREADS) ================= */}
      <g id="conveyor-top-belt">
        {/* Belt Base Rubber Mat */}
        <rect
          x="18"
          y="47.5"
          width={effectiveLength - 36}
          height="4.5"
          rx="1"
          fill={`url(#beltRubberGrad-${uid})`}
          stroke="#020617"
          strokeWidth="0.8"
        />

        {/* Moving Chevron/Grip Tread Texture */}
        <line
          x1="22"
          y1="49.5"
          x2={effectiveLength - 22}
          y2="49.5"
          stroke="#475569"
          strokeWidth="2"
          strokeDasharray="8 6"
          strokeLinecap="round"
          style={{
            animation: isMoving
              ? `${isReverse ? `beltTreadRev_${uid}` : `beltTreadFwd_${uid}`} ${beltDuration}s linear infinite`
              : 'none',
          }}
        />

        {/* Top Edge Specular Lighting Streak */}
        <line
          x1="20"
          y1="47.8"
          x2={effectiveLength - 20}
          y2="47.8"
          stroke="rgba(255, 255, 255, 0.4)"
          strokeWidth="0.8"
          strokeLinecap="round"
        />
      </g>

      {/* ================= 7. SIDE GUIDE RAILS (PRODUCT GUARDS) ================= */}
      <g id="conveyor-guide-rails">
        {/* Support Bracket Uprights */}
        {bracketPositions.map((bx) => (
          <g key={bx} transform={`translate(${bx}, 41)`}>
            {/* Vertical bracket bar */}
            <rect x="-1.5" y="0" width="3" height="13" rx="0.8" fill="#475569" stroke="#1e293b" strokeWidth="0.6" />
            {/* Clamping hex bolt */}
            <circle cx="0" cy="9" r="1.2" fill="#cbd5e1" stroke="#0f172a" strokeWidth="0.5" />
          </g>
        ))}

        {/* Stainless Steel Tubular Side Rail Bar */}
        <rect
          x="16"
          y="39"
          width={effectiveLength - 32}
          height="3"
          rx="1.5"
          fill="#cbd5e1"
          stroke="#475569"
          strokeWidth="0.8"
        />
        {/* Tube Specular Highlight */}
        <line
          x1="18"
          y1="39.8"
          x2={effectiveLength - 18}
          y2="39.8"
          stroke="#ffffff"
          strokeWidth="0.8"
          strokeLinecap="round"
          opacity="0.8"
        />
      </g>

      {/* ================= 8. INTEGRATED DRIVE MOTOR & GEARBOX ================= */}
      <g id="conveyor-drive-motor" transform={`translate(${effectiveLength - 44}, 54)`}>
        {/* Gearbox Housing (Helical-Worm Reducer attached to Head Shaft) */}
        <rect
          x="-10"
          y="0"
          width="26"
          height="24"
          rx="3"
          fill={`url(#gearboxGrad-${uid})`}
          stroke="#0f172a"
          strokeWidth="1"
        />

        {/* Gearbox Cast Ribs */}
        <line x1="-6" y1="5" x2="12" y2="5" stroke="#475569" strokeWidth="1" />
        <line x1="-6" y1="12" x2="12" y2="12" stroke="#475569" strokeWidth="1" />
        <line x1="-6" y1="19" x2="12" y2="19" stroke="#1e293b" strokeWidth="1" />

        {/* Oil Level Sight Glass */}
        <circle cx="-3" cy="12" r="3" fill="#ca8a04" stroke="#e2e8f0" strokeWidth="0.8" />
        <circle cx="-3" cy="12" r="1.5" fill="#fef08a" />

        {/* Torque Arm Bushing Bolt */}
        <circle cx="10" cy="18" r="2.2" fill="#0f172a" />
        <circle cx="10" cy="18" r="1.2" fill="#94a3b8" />

        {/* 3-Phase Electric Drive Motor Body (Stator Frame) */}
        <g transform="translate(14, 2)">
          {/* Stator Cylindrical Frame */}
          <rect
            x="0"
            y="0"
            width="24"
            height="18"
            rx="2.5"
            fill={`url(#motorStatorGrad-${uid})`}
            stroke="#0f172a"
            strokeWidth="1"
          />

          {/* Stator Longitudinal Cooling Fins */}
          <line x1="2" y1="4" x2="20" y2="4" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
          <line x1="2" y1="9" x2="20" y2="9" stroke="rgba(0,0,0,0.5)" strokeWidth="1" />
          <line x1="2" y1="14" x2="20" y2="14" stroke="rgba(0,0,0,0.6)" strokeWidth="1" />

          {/* Rear Fan Cowl / Shroud with Ventilation Slots */}
          <path
            d="M 22 1 Q 25 1 25 9 Q 25 17 22 17 Z"
            fill="#1e293b"
            stroke="#0f172a"
            strokeWidth="0.8"
          />
          {/* Air Intake Louvers */}
          <line x1="23" y1="4" x2="23" y2="14" stroke="#090d16" strokeWidth="0.8" />

          {/* Motor Terminal Connection Box (Conduit Junction Box) */}
          <rect
            x="5"
            y="-6"
            width="12"
            height="7"
            rx="1.5"
            fill="#1e293b"
            stroke="#475569"
            strokeWidth="0.8"
          />
          {/* Warning Bolt ⚡ */}
          <text
            x="11"
            y="-1"
            fill="#facc15"
            fontSize="5.5"
            fontWeight="bold"
            textAnchor="middle"
            dominantBaseline="central"
          >
            ⚡
          </text>
          {/* Cable Gland Connector */}
          <rect x="17" y="-4.5" width="2.5" height="4" rx="0.5" fill="#090d16" />
        </g>

        {/* DRIVE STATUS BEACON LED */}
        <g transform="translate(4, -3)">
          {/* LED Bezel */}
          <circle cx="0" cy="0" r="3.2" fill="#0f172a" stroke="#64748b" strokeWidth="0.8" />
          {/* Active Glowing LED Core */}
          <circle
            cx="0"
            cy="0"
            r="2"
            fill={statusColor}
            filter={isRunning || isJammed ? `url(#statusGlow-${uid})` : 'none'}
            style={{
              animation: isRunning || isJammed ? `ledPulse_${uid} 1.2s ease-in-out infinite` : 'none',
            }}
          />
        </g>
      </g>
    </svg>
  );
};
